import useUser from "@/users/useUser";
import useGetUserProperties from "@/users/v3/useGetUserProperties";
import contentTypes from "@/view/constants/contentTypes";
import { GenerationGeniusContent } from "@/view/GeniusContent/GeniusContent.interface";
import GeniusStandardsRow from "@/view/GeniusContent/GeniusStandardsRow";
import { DISABLED_FEATURES } from "@/view/SecondaryActions/SecondaryActions";
import SecondaryActionsToolbar from "@/view/SecondaryActionsToolbar";
import { IBannerMode } from "@/view/UnlevelledContent/UnlevelledContent.interface";
import getBestMatchingMaturityBands from "@/view/utils/getBestMatchingMaturityBands";
import { getMaturityRange } from "@/view/utils/getBestMatchingMaturityBands/getBestMatchingMaturityBands";
import GenerationGeniusLogo from "./GenerationGeniusLogo";
import {
  headerCss,
  labelCss,
  logoCss,
  metadataContainerCss,
  metadataCss,
  metadataItemCss,
  titleCss,
  valueCss,
} from "./GeniusContentHeader.style";

interface GeniusContentHeaderProps {
  content: GenerationGeniusContent;
  duration: string;
  bannerMode: IBannerMode;
}

const GeniusContentHeader = ({
  content,
  duration,
  bannerMode,
}: GeniusContentHeaderProps) => {
  const contentData = content.data;
  const {
    title,
    maturity_range: maturityRange,
    author = "Generation Genius",
  } = contentData;

  const getRecommendedValue = (maturityRange: number[]) => {
    if (maturityRange) {
      const matchingMaturityBands = getBestMatchingMaturityBands(maturityRange);
      return getMaturityRange(matchingMaturityBands);
    }

    return "";
  };

  const recommendedFor = getRecommendedValue(maturityRange);
  const metadata = { by: author, duration, "Recommended For": recommendedFor };
  const { data: user } = useUser();
  const {
    isStudent = false,
    isTeacher = false,
    isParent = false,
  } = useGetUserProperties();

  const standards = content.page_viewmodel?.standards || [];

  const disableFeatures: DISABLED_FEATURES[] = [
    "add-to-text-set",
    "listen-mode",
    "paragraph-count",
    "present-mode",
    "translation",
  ];

  const shouldShowStandards = isTeacher && standards.length > 0;

  return (
    <div css={headerCss}>
      <GenerationGeniusLogo css={logoCss} />
      <h1 css={titleCss}>{title}</h1>
      <div css={metadataContainerCss}>
        <div css={metadataCss}>
          {Object.entries(metadata).map(([label, value]) => {
            if (label !== "Recommended For") {
              return (
                <div css={metadataItemCss} key={label}>
                  <span css={labelCss}>{label}</span>
                  <span css={valueCss}>{value}</span>
                </div>
              );
            }
            return null;
          })}
        </div>
        {recommendedFor && (
          <div css={metadataCss}>
            <div css={metadataItemCss}>
              <span css={labelCss}>Recommended For</span>
              <span css={valueCss}>{recommendedFor}</span>
            </div>
          </div>
        )}
        {/* Standards - only visible to teachers */}
        {shouldShowStandards && <GeniusStandardsRow standards={standards} />}
      </div>
      <SecondaryActionsToolbar
        bannerMode={bannerMode}
        content={{
          ...content,
          data: {
            ...contentData,
            article_type: contentTypes.GENERATION_GENIUS_VIDEO,
            content_id: content.id,
            thumbnail: contentData.thumbnail || "",
          },
        }}
        matchedAssignments={[]}
        user={user}
        userProperties={{ isStudent, isTeacher, isParent }}
        disabledFeatures={disableFeatures}
        currentLevel={undefined}
        readAloudButtonRef={undefined}
      />
    </div>
  );
};

export default GeniusContentHeader;
